import { Scene, serialize, UniversalCamera, Vector3 } from '@babylonjs/core';
import { ICamera } from 'Framework/Components/Controls/XView/Cameras/ICamera';
import { DefaultCoordinates, ICartesianCoordinates, ICoordinates } from 'Framework/Components/Controls/XView/Core';
import { Camera360MouseInput, Camera360PointersInput } from 'Framework/Components/Controls/XView/Inputs';
import { MathHelper } from 'Framework/Helpers/MathHelper';

const Epsilon = 0.001;

export class Sphere360Camera extends UniversalCamera implements ICamera {
    /**
     * Current inertia value on the radius axis.
     * The bigger this number the longer it will take for the camera to stop.
     */
    @serialize()
    public inertialRadiusOffset: number = 0;
    public maxFov: number = Math.PI / 2; //90°
    public minFov: number = Math.PI / 180; //1°

    private _camera360MouseInput: Camera360MouseInput = null;
    private _camera360TouchInput: Camera360PointersInput = null;

    public constructor(name: string, scene: Scene) {
        super(name, Vector3.Zero(), scene);
        this.inputs.clear();
        this.addMouse(false);
        this.addTouch();
        this._setupInputs();
    }

    /**
     * Defines the target the camera should look at,
     * @param position Cartesian coordinates, if null or undefined a zero value will be apply.
     */
    public lookTo(position: Vector3 | ICoordinates): void {
        let coordinates: Vector3 = null;

        if (position instanceof Vector3) {
            coordinates = position;
        }
        else {
            let cartesianCoordinates: ICartesianCoordinates = null;

            if (MathHelper.isSphericalCoordinates(position)) {
                cartesianCoordinates = MathHelper.toCartesianCoordinates(position);
            }
            else if (MathHelper.isCartesianCoordinates(position)) {
                cartesianCoordinates = position;
            }
            else {
                cartesianCoordinates = DefaultCoordinates;
            }

            //ASTM E57 != BABYLON Vector3 Coordinates system
            coordinates = new Vector3(
                //Adding 0 prevent the result from being "-0" if cartesianCoordinates == 0,
                //which would cause a 180° rotation due to the way that camera.setTarget
                //and math.atan2 are implemented
                -cartesianCoordinates.y + 0,
                cartesianCoordinates.z,
                cartesianCoordinates.x
            );
        }

        this.setTarget(coordinates);
    }

    /** @hidden */
    public _checkInputs(): void {

        if (this.inertialRadiusOffset !== 0) {

            this.fov -= this.inertialRadiusOffset;
            this.inertialRadiusOffset *= this.inertia;

            if (Math.abs(this.inertialRadiusOffset) < this.speed * Epsilon) {
                this.inertialRadiusOffset = 0;
            }
        }
        // Limits
        this._checkLimits();

        super._checkInputs();
    }

    protected _checkLimits() {

        if (this.minFov !== null && this.fov < this.minFov) {
            this.fov = this.minFov;
            this.inertialRadiusOffset = 0;
        }
        if (this.maxFov !== null && this.fov > this.maxFov) {
            this.fov = this.maxFov;
            this.inertialRadiusOffset = 0;
        }
    }

    /**
     * Add mouse input support to the input manager.
     * @param touchEnabled if the FreeCameraMouseInput should support touch (default: true)
     * @returns the current input manager
     */
    private addMouse(touchEnabled = true): void {
        if (this.inputs) {
            this._camera360MouseInput = new Camera360MouseInput(touchEnabled);
            this._camera360MouseInput.wheelPrecision = 1000;
            this.inputs.add(this._camera360MouseInput);
        }
    }

    private addTouch(): void {
        if (this.inputs) {
            this._camera360TouchInput = new Camera360PointersInput();
            this.inputs.add(this._camera360TouchInput);
        }
    }
}
